import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound, MailCheck, Ticket } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import { apiClient } from "@/services/api";
import { useToast } from "@/hooks/useToast";

type AcceptanceMode = "link" | "code";

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["auth", "common"]);
  const toast = useToast();
  const token = searchParams.get("token") ?? "";

  const [mode, setMode] = useState<AcceptanceMode>(token ? "link" : "code");
  const [email, setEmail] = useState("");
  const [fallbackCode, setFallbackCode] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(Boolean(token));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setIsPreviewLoading(false);
      return;
    }

    let cancelled = false;

    const loadInvitation = async () => {
      setIsPreviewLoading(true);
      try {
        const preview = await apiClient.getInvitationByToken(token);
        if (!cancelled) {
          setEmail(preview.invitation.email);
          setMode("link");
          setError("");
        }
      } catch (err: unknown) {
        const apiError = err as {
          response?: {
            data?: { error?: { message?: string }; message?: string };
          };
        };
        if (!cancelled) {
          setMode("code");
          setError(
            apiError.response?.data?.error?.message ||
              apiError.response?.data?.message ||
              t("auth:invitationInvalid"),
          );
        }
      } finally {
        if (!cancelled) {
          setIsPreviewLoading(false);
        }
      }
    };

    loadInvitation();

    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const handleFallbackCodeChange = (value: string) => {
    setFallbackCode(value.replace(/\D/g, "").slice(0, 8));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");

    if (mode === "link" && !token) {
      setError(t("auth:invitationInvalid"));
      return;
    }

    if (mode === "code" && fallbackCode.length !== 8) {
      setError(t("auth:invitationFallbackCodeInvalid"));
      return;
    }

    setIsLoading(true);
    try {
      const accepted = await apiClient.acceptInvitation(
        mode === "link"
          ? {
              token,
              givenName,
              familyName,
            }
          : {
              email,
              fallbackCode,
              givenName,
              familyName,
            },
      );

      navigate(
        `/change-password?token=${encodeURIComponent(
          accepted.changePasswordToken,
        )}`,
        { replace: true },
      );
    } catch (err: unknown) {
      const apiError = err as {
        response?: {
          data?: { error?: { message?: string }; message?: string };
        };
      };
      const message =
        apiError.response?.data?.error?.message ||
        apiError.response?.data?.message ||
        t("auth:invitationAcceptError");
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50 flex flex-col">
      <Navbar hideSidebarButtons={true} hideLoginLink={true} />

      <div className="flex-1 px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-center">
          <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-[0_24px_80px_rgba(17,24,39,0.08)]">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-500/40 to-transparent" />

            <section className="p-6 sm:p-8 lg:p-10">
              <div className="mb-8 flex items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-secondary-400">
                    Race Manager
                  </p>
                  <h1 className="mt-3 text-[30px] font-bold tracking-[-0.03em] text-secondary-900 sm:text-[34px]">
                    {t("auth:acceptInvitation")}
                  </h1>
                  <p className="mt-3 max-w-md text-sm leading-6 text-secondary-600">
                    {t("auth:acceptInvitationDescription")}
                  </p>
                </div>

                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-secondary-200 bg-secondary-50 text-primary-600">
                  <MailCheck className="h-5 w-5" />
                </div>
              </div>

              <div className="mb-6 grid grid-cols-2 gap-2 rounded-lg bg-secondary-100 p-1">
                <button
                  type="button"
                  onClick={() => setMode("link")}
                  disabled={!token}
                  className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${
                    mode === "link"
                      ? "bg-white text-secondary-900 shadow-sm"
                      : "text-secondary-600 hover:text-secondary-900"
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  <MailCheck className="h-4 w-4" />
                  {t("auth:invitationLink")}
                </button>
                <button
                  type="button"
                  onClick={() => setMode("code")}
                  className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${
                    mode === "code"
                      ? "bg-white text-secondary-900 shadow-sm"
                      : "text-secondary-600 hover:text-secondary-900"
                  }`}
                >
                  <Ticket className="h-4 w-4" />
                  {t("auth:fallbackCode")}
                </button>
              </div>

              {isPreviewLoading ? (
                <div className="flex items-center justify-center py-12 text-sm text-secondary-600">
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-primary-600" />
                  {t("common:loading")}
                </div>
              ) : (
                <form className="space-y-6" onSubmit={handleSubmit}>
                  {error && (
                    <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="givenName"
                        className="block text-sm font-medium text-secondary-700"
                      >
                        {t("auth:givenName")}
                      </label>
                      <input
                        id="givenName"
                        name="givenName"
                        type="text"
                        required
                        className="input mt-2"
                        value={givenName}
                        onChange={(event) => setGivenName(event.target.value)}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="familyName"
                        className="block text-sm font-medium text-secondary-700"
                      >
                        {t("auth:familyName")}
                      </label>
                      <input
                        id="familyName"
                        name="familyName"
                        type="text"
                        required
                        className="input mt-2"
                        value={familyName}
                        onChange={(event) => setFamilyName(event.target.value)}
                      />
                    </div>
                  </div>

                  {(mode === "code" || email) && (
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-secondary-700"
                      >
                        {t("auth:email")}
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required={mode === "code"}
                        readOnly={mode === "link"}
                        className="input mt-2 read-only:bg-secondary-50"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                      />
                    </div>
                  )}

                  {mode === "code" && (
                    <div>
                      <label
                        htmlFor="fallbackCode"
                        className="block text-sm font-medium text-secondary-700"
                      >
                        {t("auth:fallbackCode")}
                      </label>
                      <input
                        id="fallbackCode"
                        name="fallbackCode"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{8}"
                        required
                        className="input mt-2 font-mono"
                        value={fallbackCode}
                        onChange={(event) =>
                          handleFallbackCodeChange(event.target.value)
                        }
                      />
                    </div>
                  )}

                  <div className="flex flex-col gap-4 border-t border-secondary-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
                    <Link
                      to="/login"
                      className="text-sm font-medium text-primary-600 hover:text-primary-500"
                    >
                      {t("auth:signIn")}
                    </Link>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn-primary min-w-[180px] px-5 py-2.5 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                          {t("common:loading")}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <KeyRound className="h-4 w-4" />
                          {t("auth:continue")}
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
